const POINT_TYPES = ['Point', 'MultiPoint'];
const LINE_TYPES = ['LineString', 'MultiLineString'];
const POLYGON_TYPES = ['Polygon', 'MultiPolygon'];

export interface BelGeometrySettings {
  pointOnly: boolean;
  polygonOnly: boolean;
  hasNonPoint: boolean;
  exceeds20km: boolean;
  showSplit: boolean;
  bufferOptional: boolean;
  splitOptional: boolean;
  bufferRequired: boolean;
  splitRequired: boolean;
  headerText: string;
  infoBannerText: string;
  bufferHelperText: string;
  bufferDefault: number;
  bufferMin: number;
  bufferMax: number;
  maxSplitDefault: number;
  maxSplitMin: number;
  maxSplitMax: number;
}

export interface BelValidateGeometrySettingsBody {
  bufferDistance: number;
  maxSplitAreaSqm?: number | null;
}

export interface BelGeometryViolation {
  featureIndex: number;
  geometryType: string;
  bufferedAreaSqm: number;
  code: string;
  message: string;
}

export interface BelGeometrySettingsValidationResult {
  canProceed: boolean;
  requiresSplit: boolean;
  maxBufferedAreaSqm?: number;
  violations: BelGeometryViolation[];
}

function polygonAreaExceedsLimit(
  geometryTypes: string[],
  hasPolygonExceeding20Km2?: boolean
): boolean {
  if (!hasPolygonGeometry(geometryTypes)) {
    return false;
  }
  return hasPolygonExceeding20Km2 === true;
}

export function getBelGeometrySettings(
  geometryTypes: string[],
  hasPolygonExceeding20Km2?: boolean
): BelGeometrySettings {
  const hasTypes = geometryTypes.length > 0;
  const pointOnly =
    hasTypes && geometryTypes.every((t) => POINT_TYPES.includes(t));
  const polygonOnly =
    hasTypes && geometryTypes.every((t) => POLYGON_TYPES.includes(t));
  const lineOnly =
    hasTypes && geometryTypes.every((t) => LINE_TYPES.includes(t));
  const hasNonPoint = geometryTypes.some((t) => !POINT_TYPES.includes(t));

  const exceeds20km = polygonAreaExceedsLimit(
    geometryTypes,
    hasPolygonExceeding20Km2
  );

  const showSplit = !pointOnly;
  const bufferOptional = polygonOnly;
  const bufferRequired = !bufferOptional;
  const splitOptional = hasNonPoint && !exceeds20km;
  const splitRequired = hasNonPoint && exceeds20km;

  let headerText = '';
  let infoBannerText = '';
  const bufferHelperText = bufferOptional
    ? 'Buffer distance is optional for this file'
    : 'Applied to all geometries in the file';

  if (pointOnly) {
    headerText = 'Buffer distance required';
    infoBannerText =
      'Points detected in your file. A buffer distance must be applied for enquiry lodgement.';
  } else if (exceeds20km) {
    if (polygonOnly) {
      headerText = 'Set maximum area per enquiry';
      infoBannerText =
        'Your file contains shapes exceeding 20 km². Set the maximum area per enquiry to split your shapes. Buffer distance is optional.';
    } else {
      headerText = 'Set maximum area and buffer distance per enquiry';
      infoBannerText =
        'Your file contains polygons exceeding 20 km². Set the maximum area per enquiry to split your shapes, and a buffer distance for lodgement.';
    }
  } else if (polygonOnly) {
    infoBannerText =
      'Your file contains polygons within the 20 km² limit. Splitting and buffer are optional.';
  } else if (lineOnly) {
    headerText = 'Set buffer distance per enquiry';
    infoBannerText =
      'Your file contains lines within the 20 km² limit. Buffer distance is required. Splitting is optional.';
  } else {
    headerText = 'Set buffer distance per enquiry';
    infoBannerText =
      'Your file contains lines and polygons within the 20 km² limit. Buffer distance is required. Splitting is optional.';
  }

  return {
    pointOnly,
    polygonOnly,
    hasNonPoint,
    exceeds20km,
    showSplit,
    bufferOptional,
    splitOptional,
    bufferRequired,
    splitRequired,
    headerText,
    infoBannerText,
    bufferHelperText,
    bufferDefault: 10,
    bufferMin: 10,
    bufferMax: 200,
    maxSplitDefault: 1.0,
    maxSplitMin: 1.0,
    maxSplitMax: 19.0
  };
}

export function hasPolygonGeometry(geometryTypes: string[]): boolean {
  return geometryTypes.some((t) => POLYGON_TYPES.includes(t));
}

export function formatGeometryDetectedLabel(
  geometryTypes: string[],
  count: number
): string {
  if (count <= 0) return '';

  const hasTypes = geometryTypes.length > 0;
  const pointOnly =
    hasTypes && geometryTypes.every((t) => POINT_TYPES.includes(t));
  const lineOnly =
    hasTypes && geometryTypes.every((t) => LINE_TYPES.includes(t));
  const polyOnly =
    hasTypes && geometryTypes.every((t) => POLYGON_TYPES.includes(t));

  let noun = 'shape';
  if (pointOnly) noun = 'point';
  else if (lineOnly) noun = 'line';
  else if (polyOnly) noun = 'polygon';

  const label = count === 1 ? noun : `${noun}s`;
  return `${count} ${label} detected`;
}

export function isBufferDistanceValid(
  value: number,
  settings: BelGeometrySettings
): boolean {
  return value >= settings.bufferMin && value <= settings.bufferMax;
}

export function isMaxSplitAreaValid(
  value: number,
  settings: BelGeometrySettings
): boolean {
  return value >= settings.maxSplitMin && value <= settings.maxSplitMax;
}

const BUFFER_DISTANCE_INPUT_PATTERN = /^\d+$/;
/** Digits, optional trailing ".", or one decimal place (e.g. 1, 1., 1.0, 1.5). */
const MAX_SPLIT_AREA_INPUT_PATTERN = /^\d+(\.\d?)?$/;
/** In-progress typing: empty, digits, trailing ".", or one decimal digit. */
const MAX_SPLIT_AREA_TYPING_PATTERN = /^(\d+(\.\d?)?)?$/;
const BUFFER_DISTANCE_TYPING_PATTERN = /^\d*$/;

export function isAllowedMaxSplitAreaTyping(raw: string): boolean {
  return MAX_SPLIT_AREA_TYPING_PATTERN.test(raw);
}

export function isAllowedBufferDistanceTyping(raw: string): boolean {
  return BUFFER_DISTANCE_TYPING_PATTERN.test(raw);
}

export function getCarbonNumberInputRaw(
  event: { imaginaryTarget?: { value?: string }; target?: { value?: string } },
  directionOrMeta?: string | { value?: number | string },
  stepperValue?: number
): string | null {
  if (typeof stepperValue === 'number' && !Number.isNaN(stepperValue)) {
    return String(stepperValue);
  }

  if (
    directionOrMeta &&
    typeof directionOrMeta === 'object' &&
    directionOrMeta.value !== undefined &&
    directionOrMeta.value !== ''
  ) {
    return String(directionOrMeta.value);
  }

  const raw = event.imaginaryTarget?.value ?? event.target?.value;
  if (raw === '' || raw === undefined || raw === null) {
    return null;
  }

  return String(raw);
}

export interface BelGeometryNumberInputParseResult {
  valid: boolean;
  value: number | null;
}

export function parseBufferDistanceRaw(
  raw: string | null | undefined,
  settings: BelGeometrySettings
): BelGeometryNumberInputParseResult {
  if (raw === null || raw === undefined || raw === '') {
    return { valid: false, value: null };
  }

  if (!BUFFER_DISTANCE_INPUT_PATTERN.test(raw)) {
    return { valid: false, value: null };
  }

  const value = parseInt(raw, 10);
  return {
    valid: isBufferDistanceValid(value, settings),
    value
  };
}

export function parseMaxSplitAreaRaw(
  raw: string | null | undefined,
  settings: BelGeometrySettings
): BelGeometryNumberInputParseResult {
  if (raw === null || raw === undefined || raw === '') {
    return { valid: false, value: null };
  }

  if (!MAX_SPLIT_AREA_INPUT_PATTERN.test(raw)) {
    return { valid: false, value: null };
  }

  // "1." normalizes to 1; stored/sent as a number, not the raw string.
  const value = Number(parseFloat(raw).toFixed(1));
  return {
    valid: isMaxSplitAreaValid(value, settings),
    value
  };
}

export function parseBufferDistanceFromCarbonInput(
  event: { imaginaryTarget?: { value?: string }; target?: { value?: string } },
  directionOrMeta: string | { value?: number | string } | undefined,
  stepperValue: number | undefined,
  settings: BelGeometrySettings
): BelGeometryNumberInputParseResult {
  const raw = getCarbonNumberInputRaw(event, directionOrMeta, stepperValue);
  return parseBufferDistanceRaw(raw, settings);
}

export function parseMaxSplitAreaFromCarbonInput(
  event: { imaginaryTarget?: { value?: string }; target?: { value?: string } },
  directionOrMeta: string | { value?: number | string } | undefined,
  stepperValue: number | undefined,
  settings: BelGeometrySettings
): BelGeometryNumberInputParseResult {
  const raw = getCarbonNumberInputRaw(event, directionOrMeta, stepperValue);
  return parseMaxSplitAreaRaw(raw, settings);
}

export function blockInvalidNumberInputKeyDown(
  event: { key: string; preventDefault: () => void },
  options: { allowDecimal?: boolean } = {}
): void {
  const blocked = ['e', 'E', '+', '-'];
  if (!options.allowDecimal) {
    blocked.push('.');
  }
  if (blocked.includes(event.key)) {
    event.preventDefault();
  }
}

export function getEffectiveGeometryValues(
  settings: BelGeometrySettings,
  applyBuffer: boolean,
  applySplit: boolean,
  bufferDistance: number,
  maxSplitAreaKm: number
): { bufferDistance: number; maxSplitAreaSqm: number | null } {
  const bufferActive = settings.bufferRequired || applyBuffer;
  const splitActive =
    settings.showSplit && (settings.splitRequired || applySplit);

  return {
    bufferDistance: bufferActive ? bufferDistance : 0,
    maxSplitAreaSqm: splitActive
      ? Math.round(maxSplitAreaKm * 1_000_000)
      : settings.showSplit
      ? 0
      : null
  };
}

export function buildValidateGeometrySettingsBody(
  settings: BelGeometrySettings,
  applyBuffer: boolean,
  applySplit: boolean,
  bufferDistance: number,
  maxSplitAreaKm: number
): BelValidateGeometrySettingsBody {
  const effective = getEffectiveGeometryValues(
    settings,
    applyBuffer,
    applySplit,
    bufferDistance,
    maxSplitAreaKm
  );

  const body: BelValidateGeometrySettingsBody = {
    bufferDistance: effective.bufferDistance
  };

  if (effective.maxSplitAreaSqm !== null) {
    body.maxSplitAreaSqm = effective.maxSplitAreaSqm;
  }

  return body;
}

export const BEL_GEOMETRY_VIOLATION_CODE = {
  BUFFERED_AREA_EXCEEDS_LIMIT: 'BUFFERED_AREA_EXCEEDS_LIMIT',
  CUMULATIVE_AREA_EXCEEDS_LIMIT: 'CUMULATIVE_AREA_EXCEEDS_LIMIT'
};

export interface BelGeometryValidationErrorBanner {
  title: string;
  message: string;
}

const GEOMETRY_AREA_TOO_LARGE_BANNER: BelGeometryValidationErrorBanner = {
  title: 'Area too large',
  message:
    'Your job area exceeds the supported limit of 20 km². Reduce the buffer distance or split the job into smaller areas.'
};

const GEOMETRY_CUMULATIVE_AREA_TOO_LARGE_BANNER: BelGeometryValidationErrorBanner = {
  title: 'Total area too large',
  message:
    'Your job total area exceeds the supported limit of 500 km². Please reduce the buffer distance.'
};

export function getGeometryValidationErrorBanner(
  violations: BelGeometryViolation[]
): BelGeometryValidationErrorBanner | null {
  if (!violations.length) {
    return null;
  }

  if (
    violations[0]?.code ===
    BEL_GEOMETRY_VIOLATION_CODE.CUMULATIVE_AREA_EXCEEDS_LIMIT
  ) {
    return GEOMETRY_CUMULATIVE_AREA_TOO_LARGE_BANNER;
  }

  return GEOMETRY_AREA_TOO_LARGE_BANNER;
}

export function formatGeometryViolationMessages(
  violations: BelGeometryViolation[]
): string {
  if (!violations.length) {
    return '';
  }
  const messages = violations.map((v) => v.message).filter(Boolean);
  return messages.length > 0
    ? messages.join(' ')
    : 'Geometry settings are invalid. Please review buffer distance and splitting options.';
}

export function parseGeometryValidationPayload(
  payload: any
): BelGeometrySettingsValidationResult | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const source =
    payload.canProceed != null
      ? payload
      : payload.details && typeof payload.details === 'object'
      ? payload.details
      : null;

  if (!source) {
    return null;
  }

  return {
    canProceed: source.canProceed === true,
    requiresSplit: source.requiresSplit === true,
    maxBufferedAreaSqm:
      typeof source.maxBufferedAreaSqm === 'number'
        ? source.maxBufferedAreaSqm
        : undefined,
    violations: Array.isArray(source.violations) ? source.violations : []
  };
}
